import { Dispatch, SetStateAction, useState } from 'react';
import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { indexTagsCategory } from '@services/queries/tagsCategory';
import { Controller, useForm } from 'react-hook-form';
import { moveTags } from '@services/queries/tags/moveTags/moveTags';
import { CategoryColorBadge } from '@components/CategoryColorBadge/CategoryColorBadge';
import { trackMoveTags } from '@services/mixpanel/trackers/TagsMetrics';
import { ITag } from '@models/Tag.model';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Line,
  Form,
  Divider,
  Label,
  BlueBold,
} from './MoveDialog.styles';

interface IMoveDialog {
  tagsIds: string[];
  open: boolean;
  onClose: () => void;
  setChecked: Dispatch<SetStateAction<string[]>>;
  setSelectedTags: Dispatch<SetStateAction<ITag[]>>;
  selectedTags: ITag[];
}

interface IFormData {
  tagCategoryId: string;
}

export const MoveDialog = ({
  open,
  onClose,
  tagsIds,
  setChecked,
  selectedTags,
  setSelectedTags,
}: IMoveDialog) => {
  const portalDiv = document.getElementById('portal');

  const queryClient = useQueryClient();

  const { data } = useQuery(['tagsCategory'], () => indexTagsCategory());

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const category = data?.find(e => e.id === selectedCategoryId);
  const filteredCategory = category ? category.name : '';

  const Options = data?.map(option => {
    return {
      value: option.id,
      label: <CategoryColorBadge color={option.color} name={option.name} />,
    };
  });

  const newData = data ?? [];

  const schema = yup.object().shape({
    tagCategoryId: yup.string().required(),
  });

  const {
    handleSubmit,
    formState: { isValid },
    setValue,
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const categoriesCount = newData
    .map(tagCategories => {
      const validTags = selectedTags.filter(
        tag => tag.tagCategoryId === tagCategories.id,
      );
      if (validTags.length === 0) return undefined;
      const tagGroup = { tagCategories, validTags };
      return tagGroup;
    })
    .filter(tagGroup => !!tagGroup);

  const affectedCategoriesNames = categoriesCount.map(e =>
    e ? e.tagCategories.name : '',
  );

  const mutationMoveTags = useMutation(moveTags, {
    onSuccess: () => {
      queryClient.invalidateQueries('tagCategories');
      trackMoveTags(
        tagsIds.length,
        categoriesCount.length,
        affectedCategoriesNames,
        filteredCategory,
      );
      onClose();
      setValue('tagCategoryId', '');
      setChecked([]);
      setSelectedTags([]);
    },
  });

  const onSubmit = (Tag: IFormData) => {
    if (!mutationMoveTags.isLoading) {
      if (data)
        mutationMoveTags.mutate({
          tagsIds,
          tagCategoryId: Tag.tagCategoryId,
        });
    }
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Move tag(s)</Title>
              <Close
                onClick={() => {
                  onClose();
                }}
              >
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider height="28px" />

            <Label>
              You are about to move{' '}
              <BlueBold>{`${tagsIds.length} tags`}</BlueBold>.
            </Label>

            <Form onSubmit={handleSubmit(onSubmit)}>
              <Label className="label">Move to</Label>

              <Controller
                control={control}
                name="tagCategoryId"
                render={({ field: { onChange, value } }) => (
                  <CustomPicker
                    options={Options}
                    placeholder="Select a Category"
                    value={Options?.find(c => c.value === value)}
                    onChange={(val: any) => {
                      onChange(val.value);
                      setSelectedCategoryId(val.value);
                    }}
                  />
                )}
              />

              <Line>
                <Confirm
                  type="submit"
                  disabled={!isValid || mutationMoveTags.isLoading}
                >
                  Move
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
